import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import OtpInput from "react-otp-input";
import { ArrowLeft, ChevronDown, Minus, Plus, Info, Loader2 } from "lucide-react";
import { forgottonPassword, verifyEmailByOtpGiftCard } from "../../reducers/authentication.slice";
import { createRedemption } from "../../reducers/giftcard.slice";
import { getUsers } from "../../utils/getUser";
import { AuthContext } from "../../context/authContext";
import { companyConfig } from "./myGiftCardList";
import { retailerTitle, giftCardTypeLabel } from "../../components/member/giftcards/GiftCardTile";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../../components/ui/collapsible";
import useMediaQuery from "../../components/shared/useMediaQuery";

const MONO = "ui-monospace, SF Mono, Menlo, Fira Code, monospace";

function InfoAccordion({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, overflow: "hidden" }}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 18px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {title}
            <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", color: "#c3c8cc" }} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div style={{ padding: "0 18px 18px", fontSize: 13, lineHeight: 1.6, color: "#c3c8cc" }}>{children}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export default function MemberGiftCard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState(1);
  const { walletSummary, isWalletRefresh, setIsWalletRefresh, showToast } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [codeErrorMessage, setCodeErrorMessage] = useState("");
  const [cardetails, setCardDetails] = useState({});
  const [formErrors, setFormErrors] = useState({
    amount: { error: false, message: "" },
    quantity: { error: false, message: "" },
  });
  const [amountDenomination, setAmountDenomination] = useState([]);
  const [redempData, setRedemData] = useState({ actualAmount: "", discountedAmount: "" });
  const [showLoader, setShowLoader] = useState(false);

  const user = getUsers();

  useEffect(() => {
    if (location?.state?.card) {
      const cardData = location.state.card;
      const denominations = cardData[1]?.gift_cards?.map((card) => card.amount) || [];
      setAmountDenomination(denominations);

      const selectedAmount = denominations.length > 0 ? denominations[0] : 0;
      const selectedCard = cardData[1]?.gift_cards?.find((card) => card.amount === selectedAmount);

      if (selectedCard) {
        const boost = selectedCard.boost || 10;
        const boostAmount = ((selectedAmount * boost) / 100).toFixed(4);
        const discountedAmount = (selectedAmount - boostAmount).toFixed(2);

        setAmount(selectedAmount);
        setRedemData({ actualAmount: selectedAmount, discountedAmount });
        setCardDetails({ ...selectedCard, discountedAmount, newTotalAmount: selectedAmount, retailer: cardData[0] });

        if (parseFloat(discountedAmount) > walletSummary?.current_balance) {
          setFieldError("quantity", true, `Insufficient balance. Available: $${walletSummary?.current_balance.toFixed(2)}`);
        } else {
          setFieldError("quantity", false);
        }
      }
    }
  }, [location?.state?.card, walletSummary?.current_balance]);

  const setFieldError = (field, hasError, message = "") => {
    setFormErrors((prev) => ({ ...prev, [field]: { error: hasError, message } }));
  };

  const handleClose = () => {
    setOpen(false);
    setVerificationCode("");
    setCodeError(false);
  };

  const handleSendOtp = () => {
    if (redempData?.discountedAmount > walletSummary?.current_balance) {
      showToast("Insufficient balance", "error");
      return;
    }
    let hasError = false;
    if (!amount) {
      setFieldError("amount", true, "Please select an amount");
      hasError = true;
    }
    const maxAllowed = cardetails?.quantity || 1;
    if (quantity > maxAllowed) {
      setFieldError("quantity", true, `Maximum available quantity is ${maxAllowed}`);
      hasError = true;
    }
    if (hasError) return;

    setCodeErrorMessage("");
    setShowLoader(true);
    dispatch(forgottonPassword({ email: user?.email, email_type: "WITHDRAWAL_OTP" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      setOpen(true);
      showToast("Verification code has been successfully sent", "success");
      setShowLoader(false);
    });
  };

  const handleVerifyCode = (event) => {
    event.preventDefault();
    if (verificationCode.length !== 6) {
      setCodeError(true);
      return;
    }
    setShowLoader(true);
    const singleCardActualAmount = amount;
    const selectedCard = location.state.card[1]?.gift_cards?.find((card) => card.amount === amount);
    const boost = selectedCard?.boost || 10;
    const boostAmount = ((singleCardActualAmount * boost) / 100).toFixed(4);
    const singleCardDiscountedAmount = (singleCardActualAmount - boostAmount).toFixed(2);

    const payload = {
      gift_card_id: cardetails?.id,
      actual_amount: parseFloat(singleCardActualAmount),
      discounted_amount: parseFloat(singleCardDiscountedAmount),
      quantity: 1,
      status: "PENDING_APPROVAL",
    };
    const request_list = [];
    for (let i = 0; i <= quantity - 1; i++) request_list.push(payload);

    dispatch(verifyEmailByOtpGiftCard({ id: user?.id, otp: verificationCode })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setCodeError(true);
        setShowLoader(false);
        setCodeErrorMessage(res?.error?.message);
        return;
      }
      dispatch(createRedemption({ request_list, otp: verificationCode })).then((res2) => {
        if (res2?.error && Object.keys(res2?.error)?.length > 0) {
          showToast(res2?.error?.message, "error");
          handleClose();
          setShowLoader(false);
          return;
        }
        setIsWalletRefresh(!isWalletRefresh);
        handleClose();
        setShowLoader(false);
        showToast("Gift card purchased successfully", "success");
        navigate("/dashboard/wallet/gift-cards");
      });
    });
  };

  const handleQuantityChange = (newValue) => {
    newValue = Number(newValue);
    const selectedCard = location.state.card[1]?.gift_cards?.find((card) => card.amount === amount);
    const maxAllowed = selectedCard?.quantity || 1;

    if (newValue <= 0) {
      setQuantity(newValue);
      setFieldError("quantity", true, newValue === 0 ? "Quantity must be at least 1" : "Quantity cannot be negative");
      return;
    }
    if (newValue > maxAllowed) {
      setQuantity(newValue);
      setFieldError("quantity", true, `Maximum available quantity is ${maxAllowed}`);
      return;
    }

    setQuantity(newValue);

    if (selectedCard) {
      const boost = selectedCard.boost || 10;
      const newTotalAmount = amount * newValue;
      const boostAmount = ((newTotalAmount * boost) / 100).toFixed(4);
      const discountedAmount = (newTotalAmount - boostAmount).toFixed(2);

      if (parseFloat(discountedAmount) > walletSummary?.current_balance) {
        setFieldError("quantity", true, `Insufficient balance. Available: $${walletSummary?.current_balance.toFixed(2)}`);
      } else {
        setFieldError("quantity", false);
      }

      setRedemData({ actualAmount: newTotalAmount, discountedAmount });
      setCardDetails((prevDetails) => ({ ...prevDetails, newTotalAmount, discountedAmount }));
    }
  };

  const handleIncrease = () => handleQuantityChange((quantity === "" ? 0 : Number(quantity)) + 1);
  const handleDecrease = () => {
    const current = quantity === "" ? 0 : Number(quantity);
    if (current > 1) handleQuantityChange(current - 1);
  };

  const handleAmountChange = (newAmount) => {
    setAmount(newAmount);
    setQuantity(1);
    const selectedCard = location.state.card[1]?.gift_cards?.find((card) => card.amount === newAmount);
    if (selectedCard) {
      const boost = selectedCard.boost || 10;
      const boostAmount = ((newAmount * 1 * boost) / 100).toFixed(4);
      const discountedAmount = (newAmount * 1 - boostAmount).toFixed(2);

      setRedemData({ actualAmount: newAmount * 1, discountedAmount });
      setCardDetails({ ...selectedCard, discountedAmount, retailer: location.state.card[0], newTotalAmount: newAmount * 1 });

      if (parseFloat(discountedAmount) > walletSummary?.current_balance) {
        setFieldError("quantity", true, `Insufficient balance. Available: $${walletSummary?.current_balance.toFixed(2)}`);
      } else {
        setFieldError("quantity", false);
      }
    }
  };

  const findBgColor = (cardType) => {
    const { logo, bg } = companyConfig[cardType] || { logo: "/images/amazon.png", bg: "#000000" };
    return { bgColor: bg, bgLogo: logo };
  };

  const bgInfo = findBgColor(cardetails?.retailer);
  const buyDisabled = formErrors.quantity.error || quantity === 0 || quantity === "";

  return (
    <div style={{ maxWidth: 640, width: "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          width: "fit-content",
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <div
        style={{
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
          background: bgInfo.bgColor
            ? `linear-gradient(160deg, ${bgInfo.bgColor}dd, ${bgInfo.bgColor}55 70%, transparent)`
            : "linear-gradient(160deg, #2a2a2e, #141416)",
          backgroundColor: bgInfo.bgColor || "#1a1a1a",
          padding: isMobile ? "24px 16px" : "30px 20px",
          textAlign: "center",
        }}
      >
        {cardetails?.boost != null && (
          <div
            style={{
              display: "inline-flex",
              padding: "3px 12px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#00ADB5",
              background: "rgba(0,173,181,0.16)",
              marginBottom: 16,
            }}
          >
            {cardetails.boost}% Boost
          </div>
        )}
        <img src={bgInfo.bgLogo} alt={cardetails.retailer} style={{ maxWidth: 140, maxHeight: 46, objectFit: "contain", display: "block", margin: "0 auto 10px" }} />
        {cardetails?.gift_card_type && (
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>
            {giftCardTypeLabel(cardetails.gift_card_type)}
          </div>
        )}
      </div>

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 16,
          padding: isMobile ? 16 : 22,
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
        }}
      >
        <h1 style={{ fontSize: 19, fontWeight: 700, color: "#fff", textAlign: "center", margin: "0 0 20px" }}>
          {retailerTitle(cardetails?.retailer)} Gift Card
        </h1>

        <div style={{ display: "flex", gap: 12, marginBottom: 8, flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ flex: "1 1 60%" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", color: "#c3c8cc", marginBottom: 6 }}>AMOUNT</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: 42,
                    padding: "0 14px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.03)",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  ${amount || 0}
                  <ChevronDown size={15} color="#c3c8cc" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent style={{ minWidth: 160 }}>
                {amountDenomination.map((value) => (
                  <DropdownMenuItem key={value} onSelect={() => handleAmountChange(value)}>
                    ${value}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {formErrors.amount.error && (
              <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6 }}>{formErrors.amount.message}</div>
            )}
          </div>

          <div style={{ flex: "1 1 40%" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", color: "#c3c8cc", marginBottom: 6 }}>QTY</div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: 42,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.03)",
                padding: "0 6px",
              }}
            >
              <button
                type="button"
                onClick={handleDecrease}
                style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "transparent", color: "#c3c8cc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <Minus size={14} />
              </button>
              <span style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: "#fff" }}>{quantity}</span>
              <button
                type="button"
                onClick={handleIncrease}
                style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "transparent", color: "#c3c8cc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <Plus size={14} />
              </button>
            </div>
            {formErrors.quantity.error && (
              <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6 }}>{formErrors.quantity.message}</div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginTop: 22 }}>
          <Button size="lg" style={{ width: "100%" }} disabled={buyDisabled} onClick={handleSendOtp}>
            Buy Now for{" "}
            <span style={{ textDecoration: "line-through", color: "rgba(0,0,0,0.45)", margin: "0 4px" }}>
              ${cardetails?.newTotalAmount || 0}
            </span>
            <span>${cardetails?.discountedAmount || 0}</span>
          </Button>
          <div
            style={{
              padding: "5px 14px",
              borderRadius: 999,
              background: "rgba(76,175,80,0.14)",
              color: "#4caf50",
              fontSize: 11.5,
              fontWeight: 700,
            }}
          >
            You are saving {cardetails?.boost}%
          </div>
        </div>

        <p style={{ fontSize: 12, color: "#c3c8cc", textAlign: "center", margin: "14px 0 0" }}>
          It may take up to 72 hours to deliver the card details.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <InfoAccordion title="Description">{cardetails?.description || "NA"}</InfoAccordion>
        <InfoAccordion title="Terms & Conditions">{cardetails?.tnc || "NA"}</InfoAccordion>
      </div>

      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent style={{ maxWidth: 440 }}>
          <DialogHeader>
            <DialogTitle>Confirm Gift Card Purchase</DialogTitle>
            <DialogDescription>
              For security purposes, please enter the 6-digit verification code sent to your registered email address.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleVerifyCode}>
            <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 12px" }}>
              <OtpInput
                value={verificationCode}
                onChange={setVerificationCode}
                numInputs={6}
                renderSeparator={<span style={{ width: 6 }} />}
                inputType="tel"
                renderInput={(props) => (
                  <input
                    {...props}
                    style={{
                      width: "clamp(32px, calc(100vw / 12), 46px)",
                      height: "clamp(38px, calc(100vw / 12), 52px)",
                      fontSize: 17,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.03)",
                      color: "#fff",
                      textAlign: "center",
                      outline: "none",
                      margin: "0 3px",
                    }}
                    autoComplete="one-time-code"
                  />
                )}
              />
            </div>
            {codeError && (
              <div style={{ fontSize: 12, color: "#f27059", textAlign: "center", marginBottom: 10 }}>
                {codeErrorMessage || "Please enter a valid verification code"}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#c3c8cc", lineHeight: 1.5 }}>
              <Info size={14} />
              We've sent a verification code to your email: {user?.email}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
              <Button type="button" variant="outline" onClick={handleClose} style={{ width: isMobile ? "100%" : "auto" }}>
                Cancel
              </Button>
              <Button type="submit" disabled={verificationCode.length !== 6} style={{ width: isMobile ? "100%" : "auto" }}>
                {showLoader ? <Loader2 size={14} className="tw-animate-spin" /> : "Verify"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
